import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  listFavorites,
  deleteFavorite,
  type FavoriteWithTargetDto,
} from '@/lib/api';
import { useTk, useLocale } from '@/lib/i18n';
import { useAuth } from '@/lib/use-auth';
import { BottomNav } from '@/components/BottomNav';

type Tab = 'MERCHANT' | 'DISCOVER_POST';

interface LoadState {
  status: 'loading' | 'success' | 'error';
  items: FavoriteWithTargetDto[];
  errorMessage: string | null;
}

function pickName(
  item: FavoriteWithTargetDto,
  locale: string,
): string {
  if (!item.target) return '—';
  if (locale === 'km-KH' && item.target.nameKm) return item.target.nameKm;
  if (locale === 'en' && item.target.nameEn) return item.target.nameEn;
  return item.target.nameZh ?? '—';
}

export function FavoritesPage(): React.ReactElement {
  const tk = useTk();
  const locale = useLocale();
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = useState<Tab>('MERCHANT');
  const [state, setState] = useState<LoadState>({
    status: 'loading',
    items: [],
    errorMessage: null,
  });
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set());

  const fetchFavorites = useCallback(
    (activeTab: Tab) => {
      if (auth.status !== 'authenticated' || !auth.token) {
        setState({ status: 'success', items: [], errorMessage: null });
        return;
      }
      const ac = new AbortController();
      setState({ status: 'loading', items: [], errorMessage: null });
      listFavorites({ type: activeTab, pageSize: 50 }, auth.token, ac.signal)
        .then((res) =>
          setState({ status: 'success', items: res.items, errorMessage: null }),
        )
        .catch((err: Error) => {
          if (ac.signal.aborted) return;
          setState({ status: 'error', items: [], errorMessage: err.message });
        });
      return () => ac.abort();
    },
    [auth.status, auth.token],
  );

  useEffect(() => {
    const cleanup = fetchFavorites(tab);
    return cleanup;
  }, [tab, fetchFavorites]);

  const handleRemove = useCallback(
    async (favoriteId: string) => {
      if (!auth.token) return;
      setRemovingIds((prev) => new Set(prev).add(favoriteId));
      // Optimistic remove
      setState((prev) => ({
        ...prev,
        items: prev.items.filter((f) => f.id !== favoriteId),
      }));
      try {
        await deleteFavorite(favoriteId, auth.token);
      } catch {
        // Rollback: re-fetch
        fetchFavorites(tab);
      }
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(favoriteId);
        return next;
      });
    },
    [auth.token, fetchFavorites, tab],
  );

  const tabs: Array<{ key: Tab; labelKey: string }> = [
    { key: 'MERCHANT', labelKey: 'me.favorites.tab_merchants' },
    { key: 'DISCOVER_POST', labelKey: 'me.favorites.tab_posts' },
  ];

  return (
    <main className="min-h-screen pb-24">
      <header className="sticky top-0 z-20 px-5 pt-5 pb-3 bg-clay-50/85 backdrop-blur-md flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="grid h-9 w-9 place-items-center rounded-full bg-white border border-clay-200 text-clay-700"
          aria-label="back"
        >
          <BackGlyph />
        </button>
        <span className="eyebrow flex-1">{tk('me.favorites.title')}</span>
      </header>

      <div className="px-5 mt-2 space-y-4">
        {auth.status !== 'authenticated' ? (
          <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
            {tk('me.favorites.unauth')}
          </div>
        ) : (
          <>
            {/* Tab bar */}
            <div className="flex gap-2">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors active:scale-95 ${
                    tab === t.key
                      ? 'bg-amber-600 text-white'
                      : 'bg-clay-100 text-clay-600'
                  }`}
                >
                  {tk(t.labelKey)}
                </button>
              ))}
            </div>

            {state.status === 'loading' ? (
              <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
                {tk('common.loading')}
              </div>
            ) : null}

            {state.status === 'error' ? (
              <div className="rounded-2xl border border-danger-500/40 bg-danger-500/10 p-4 text-sm text-danger-500">
                <p className="font-medium">{tk('common.error.generic')}</p>
                <p className="text-xs opacity-75 mt-1 break-all">
                  {state.errorMessage}
                </p>
              </div>
            ) : null}

            {state.status === 'success' && state.items.length === 0 ? (
              <div className="surface-card border-dashed p-12 text-center text-clay-500 text-sm">
                {tab === 'MERCHANT'
                  ? tk('me.favorites.empty_merchants')
                  : tk('me.favorites.empty_posts')}
              </div>
            ) : null}

            {state.status === 'success' && state.items.length > 0 ? (
              <ul className="space-y-3">
                {state.items.map((fav) => (
                  <li key={fav.id}>
                    <div className="surface-card overflow-hidden">
                      {tab === 'MERCHANT' ? (
                        <Link
                          to={`/merchants/${fav.targetId}`}
                          className="flex items-center gap-4 p-4 active:scale-[0.99] transition-transform"
                        >
                          {fav.target?.coverImageUrl ? (
                            <div
                              className="h-16 w-16 shrink-0 rounded-xl bg-clay-100 bg-cover bg-center"
                              style={{
                                backgroundImage: `url(${fav.target.coverImageUrl})`,
                              }}
                              role="img"
                              aria-label={pickName(fav, locale)}
                            />
                          ) : (
                            <div
                              className="h-16 w-16 shrink-0 rounded-xl grid place-items-center"
                              style={{
                                background:
                                  'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
                              }}
                              aria-hidden
                            >
                              <span className="font-serif text-xl font-semibold text-clay-900/70">
                                {(pickName(fav, locale) || '?').slice(0, 1)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-clay-900 truncate">
                              {pickName(fav, locale)}
                            </h3>
                            <p className="text-xs text-clay-500 mt-0.5">
                              {tk('merchant.action.favorited')}
                            </p>
                          </div>
                        </Link>
                      ) : (
                        <Link
                          to={`/merchants/${fav.targetId}`}
                          className="flex items-center gap-4 p-4 active:scale-[0.99] transition-transform"
                        >
                          {fav.target?.coverImageUrl ? (
                            <div
                              className="h-16 w-16 shrink-0 rounded-xl bg-clay-100 bg-cover bg-center"
                              style={{
                                backgroundImage: `url(${fav.target.coverImageUrl})`,
                              }}
                              role="img"
                              aria-label={pickName(fav, locale)}
                            />
                          ) : (
                            <div
                              className="h-16 w-16 shrink-0 rounded-xl grid place-items-center"
                              style={{
                                background:
                                  'linear-gradient(135deg, #FFE3BD 0%, #FFB050 60%, #E5741F 100%)',
                              }}
                              aria-hidden
                            >
                              <span className="font-serif text-xl font-semibold text-clay-900/70">
                                {(pickName(fav, locale) || '?').slice(0, 1)}
                              </span>
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-clay-900 truncate">
                              {pickName(fav, locale)}
                            </h3>
                            <p className="text-xs text-clay-500 mt-0.5">
                              {tk('discover.title')}
                            </p>
                          </div>
                        </Link>
                      )}
                      <div className="border-t border-clay-100 px-4 py-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleRemove(fav.id)}
                          disabled={removingIds.has(fav.id)}
                          className="rounded-full bg-clay-50 px-3 py-1.5 text-xs font-medium text-clay-600 active:scale-95 transition-colors hover:bg-danger-500/10 hover:text-danger-500"
                        >
                          {tk('merchant.action.unfavorite')}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>

      <BottomNav />
    </main>
  );
}

function BackGlyph(): React.ReactElement {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M10 4l-4 4 4 4" />
    </svg>
  );
}
